import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Loader2, Mail, ExternalLink, Users, ArrowRight, Filter, RefreshCw, Inbox, Sparkles, Send, Calendar, Clock, Lock, Unlock, MessageCircle, Wand2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
// iter 155 — RequestScreeningButton import removed (employer-initiated screening parked for v1).
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import MessageComposeDialog from "@/components/messaging/MessageComposeDialog";

const STATUS_STYLES = {
    new: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
    reviewing: "bg-sky-500/15 text-sky-700 dark:text-sky-400",
    contacted: "bg-violet-500/15 text-violet-700 dark:text-violet-400",
    rejected: "bg-rose-500/15 text-rose-700 dark:text-rose-400",
    hired: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
};
const STATUS_OPTIONS = ["new", "reviewing", "contacted", "hired", "rejected"];

const EmployerApplications = () => {
    const [rows, setRows] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [jobFilter, setJobFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    // Smart Reply modal state
    const [replyFor, setReplyFor] = useState(null); // application row
    const [variants, setVariants] = useState([]);
    const [variantsLoading, setVariantsLoading] = useState(false);
    const [pickedIdx, setPickedIdx] = useState(0);
    const [replyDraft, setReplyDraft] = useState({ subject: "", body: "" });
    const [bookingLink, setBookingLink] = useState("");
    const [suggestedTimes, setSuggestedTimes] = useState([]);
    const [existingReservations, setExistingReservations] = useState([]);
    const [reservingIso, setReservingIso] = useState(null);
    const [chatReservation, setChatReservation] = useState(null);
    const [sending, setSending] = useState(false);
    // iter-165 — In-app Message composer target (the applicant row).
    const [messageTarget, setMessageTarget] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (jobFilter !== "all") params.job_id = jobFilter;
            if (statusFilter !== "all") params.status = statusFilter;
            const { data } = await api.get("/employer/applications", { params });
            setRows(data.results || []);
            setJobs(data.jobs || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [jobFilter, statusFilter]);
    useEffect(() => { load(); }, [load]);

    const updateStatus = async (id, status) => {
        setBusy(id);
        try {
            await api.patch(`/employer/applications/${id}`, { status });
            setRows((r) => r.map((x) => (x.id === id ? { ...x, status } : x)));
            toast.success(`Marked as ${status}.`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    };

    const openSmartReply = async (row) => {
        setReplyFor(row);
        setVariants([]);
        setPickedIdx(0);
        setReplyDraft({ subject: "", body: "" });
        setVariantsLoading(true);
        try {
            const { data } = await api.post(`/employer/applications/${row.id}/smart-replies`);
            const vs = data.variants || [];
            setVariants(vs);
            setBookingLink(data.booking_link || "");
            setSuggestedTimes(data.suggested_times || []);
            setExistingReservations(data.existing_reservations || []);
            if (vs.length) setReplyDraft({ subject: vs[0].subject, body: vs[0].body });
        } catch (err) { toast.error(formatApiError(err)); setReplyFor(null); }
        finally { setVariantsLoading(false); }
    };

    const insertBookingLink = () => {
        if (!bookingLink) return;
        const body = replyDraft.body || "";
        if (body.includes(bookingLink)) {
            toast.info("Booking link is already in the message.");
            return;
        }
        setReplyDraft({ ...replyDraft, body: `${body}\n\nPick a time that works for you: ${bookingLink}` });
        toast.success("Booking link added.");
    };

    const insertSuggestedTime = (slot) => {
        // Convert the UTC ISO to the interviewer's own timezone label so the
        // candidate reads "Wed Aug 6 · 3:00 PM CEST" instead of raw UTC.
        const d = new Date(slot.iso);
        const nice = d.toLocaleString(undefined, {
            weekday: "short", month: "short", day: "numeric",
            hour: "numeric", minute: "2-digit", timeZoneName: "short",
        });
        // Prefer the direct-book link (?prefer=<iso>) — one click lands the
        // candidate on the public page with this exact slot pre-selected.
        const line = slot.link
            ? `• ${nice} — grab this time: ${slot.link}`
            : `• ${nice}`;
        const body = replyDraft.body || "";
        if (body.includes(nice)) {
            toast.info("That time is already in the message.");
            return;
        }
        setReplyDraft({ ...replyDraft, body: body ? `${body}\n${line}` : line });
        toast.success("Time added to draft.");
    };

    const reserveSlot = async (iso) => {
        if (!replyFor) return;
        setReservingIso(iso);
        try {
            const { data } = await api.post(`/employer/applications/${replyFor.id}/reserve-slot`, { iso });
            // Move the reserved slot into `existingReservations`, drop from open list.
            setSuggestedTimes((prev) => prev.filter((s) => s.iso !== iso));
            const r = data.reservation;
            // Backend returns the persisted doc — reformat like _list_reservations_for_app.
            const nd = new Date(r.start_iso);
            const label = nd.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
            const link = bookingLink ? `${bookingLink}?prefer=${encodeURIComponent(r.start_iso)}&res=${r.token}` : "";
            setExistingReservations((prev) => [{ id: r.id, token: r.token, iso: r.start_iso, label, expires_at: r.expires_at, link }, ...prev]);
            toast.success("Slot reserved for 24h. Direct link ready.");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setReservingIso(null); }
    };

    const releaseSlot = async (reservation) => {
        if (!replyFor) return;
        try {
            await api.delete(`/employer/applications/${replyFor.id}/reserve-slot/${reservation.id}`);
            setExistingReservations((prev) => prev.filter((r) => r.id !== reservation.id));
            toast.success("Slot released.");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const pickVariant = (i) => {
        setPickedIdx(i);
        const v = variants[i];
        if (v) setReplyDraft({ subject: v.subject, body: v.body });
    };

    const sendReply = async () => {
        if (!replyDraft.subject.trim() || !replyDraft.body.trim()) {
            toast.error("Subject and body can't be empty.");
            return;
        }
        setSending(true);
        try {
            const { data } = await api.post(`/employer/applications/${replyFor.id}/send-reply`, replyDraft);
            if (data.success) {
                toast.success("Reply sent.");
                // Also flip the applicant to Contacted for tidiness.
                await updateStatus(replyFor.id, "contacted");
                setReplyFor(null);
            }
        } catch (err) {
            // 502 from the backend now carries the Resend rejection message
            // so employers see WHY the send failed (bad recipient, unverified domain…).
            toast.error(formatApiError(err));
        }
        finally { setSending(false); }
    };

    const counts = useMemo(() => {
        const c = { new: 0, reviewing: 0, contacted: 0, rejected: 0, hired: 0 };
        rows.forEach((r) => { if (c[r.status] !== undefined) c[r.status] += 1; });
        return c;
    }, [rows]);

    return (
        <div data-testid="employer-applications-page" className="space-y-6">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <p className="overline">Applicants</p>
                    <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">Application Tracker</h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                        Everyone who&apos;s applied to your live jobs. Move applicants through Reviewing → Contacted → Hired to keep your pipeline tight.
                    </p>
                </div>
                <button onClick={load} disabled={loading} data-testid="apps-refresh" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-60">
                    <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>

            {/* Status snapshot */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {STATUS_OPTIONS.map((s) => (
                    <button
                        key={s}
                        onClick={() => setStatusFilter(statusFilter === s ? "all" : s)}
                        data-testid={`apps-count-${s}`}
                        className={`card-soft p-3 text-left transition-colors ${statusFilter === s ? "border-emerald-500/60 ring-1 ring-emerald-500/40" : ""}`}
                    >
                        <div className="text-xs uppercase tracking-wider text-muted-foreground">{s}</div>
                        <div className="mt-1 text-2xl font-heading font-semibold text-foreground">{counts[s] || 0}</div>
                    </button>
                ))}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                    <Filter size={13} /> Filter by job
                </div>
                <Select value={jobFilter} onValueChange={setJobFilter}>
                    <SelectTrigger data-testid="apps-job-filter" className="h-9 w-64 bg-background text-sm">
                        <SelectValue placeholder="All jobs" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All jobs</SelectItem>
                        {jobs.map((j) => (
                            <SelectItem key={j.id} value={j.id}>
                                {j.title} — {j.location}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {statusFilter !== "all" && (
                    <button onClick={() => setStatusFilter("all")} data-testid="apps-clear-status" className="text-xs font-semibold text-emerald-700 hover:underline">
                        Clear status: {statusFilter}
                    </button>
                )}
            </div>

            {/* Table */}
            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : rows.length === 0 ? (
                <div data-testid="apps-empty" className="card-soft p-12 text-center">
                    <Inbox size={22} className="mx-auto text-emerald-500" />
                    <h3 className="mt-3 font-heading font-semibold text-base text-foreground">No applications yet.</h3>
                    <p className="mt-1.5 text-sm text-muted-foreground max-w-md mx-auto">
                        Once talent applies to your live jobs, they&apos;ll show up here with resume, contact info, and a stage tracker.
                    </p>
                    <Link to="/employer/jobs/new" className="btn-green mt-5 inline-flex text-sm">
                        Post another role <ArrowRight size={13} className="ml-1.5" />
                    </Link>
                </div>
            ) : (
                <div className="card-soft overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/40">
                                <tr className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                    <th className="px-4 py-3">Applicant</th>
                                    <th className="px-4 py-3">Job</th>
                                    <th className="px-4 py-3">Applied</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <motion.tr
                                        key={r.id}
                                        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                                        data-testid={`apps-row-${r.id}`}
                                        className="border-t border-border hover:bg-muted/30"
                                    >
                                        <td className="px-4 py-3">
                                            <div className="font-semibold text-foreground">{r.name}</div>
                                            <a href={`mailto:${r.email}`} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mt-0.5">
                                                <Mail size={11} /> {r.email}
                                            </a>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="text-sm text-foreground">{r.job_title}</div>
                                            {r.company && <div className="text-xs text-muted-foreground">{r.company}</div>}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-muted-foreground">
                                            {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)} disabled={busy === r.id}>
                                                <SelectTrigger data-testid={`apps-status-${r.id}`} className={`h-8 w-32 text-xs border ${STATUS_STYLES[r.status] || ""}`}>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {STATUS_OPTIONS.map((s) => (
                                                        <SelectItem key={s} value={s}>{s}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            {r.cover_letter && (
                                                <Badge variant="outline" className="text-[10px] mr-2">Cover</Badge>
                                            )}
                                            <div className="inline-flex items-center gap-2 flex-wrap justify-end">
                                                <button
                                                    onClick={() => openSmartReply(r)}
                                                    data-testid={`apps-smart-reply-${r.id}`}
                                                    className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/15"
                                                >
                                                    <Sparkles size={10} /> AI Reply
                                                </button>
                                                {r.talent_user_id && (
                                                    <button
                                                        onClick={() => setMessageTarget(r)}
                                                        data-testid={`apps-message-${r.id}`}
                                                        className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground hover:bg-muted/40"
                                                        title="Send in-app message"
                                                    >
                                                        <MessageCircle size={10} /> Message
                                                    </button>
                                                )}
                                                {/* iter 155 — v1 verification simplification. Employer-initiated
                                                    background screening is PARKED for v1. The RequestScreeningButton
                                                    entry point is removed from the user-facing surface. The route /
                                                    component / backend endpoints still exist for the admin recruit
                                                    drawer and manual ops use only. See /app/memory/CHANGELOG.md. */}
                                                <a
                                                    href={`mailto:${r.email}?subject=${encodeURIComponent(`Re: ${r.job_title || "your application"}`)}`}
                                                    data-testid={`apps-email-${r.id}`}
                                                    className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400 hover:underline"
                                                >
                                                    Email <ExternalLink size={11} />
                                                </a>
                                            </div>
                                        </td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Smart Reply modal — LLM-drafted variants + editable send */}
            <Dialog open={replyFor !== null} onOpenChange={(open) => !open && setReplyFor(null)}>
                <DialogContent data-testid="smart-reply-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Sparkles size={16} className="text-emerald-500" /> Smart Reply drafts
                        </DialogTitle>
                        <DialogDescription>
                            {replyFor && (
                                <span>Three AI-drafted variants for <strong>{replyFor.name}</strong> — pick, edit, send.</span>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    {variantsLoading ? (
                        <div className="py-12 flex flex-col items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 size={18} className="animate-spin text-emerald-500" /> Drafting variants…
                        </div>
                    ) : variants.length === 0 ? (
                        <div className="py-8 text-center text-sm text-muted-foreground">No drafts generated.</div>
                    ) : (
                        <>
                            <div className="grid grid-cols-3 gap-2">
                                {variants.map((v, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => pickVariant(i)}
                                        data-testid={`smart-reply-variant-${i}`}
                                        className={`p-3 text-left rounded-lg border transition-colors ${pickedIdx === i ? "border-emerald-500/60 bg-emerald-500/10" : "border-border bg-card hover:border-emerald-500/30"}`}
                                    >
                                        <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 dark:text-emerald-400">{v.tone}</div>
                                        <div className="mt-1 text-xs font-semibold text-foreground line-clamp-2">{v.subject}</div>
                                        <div className="mt-1 text-[11px] text-muted-foreground line-clamp-3">{v.body}</div>
                                    </button>
                                ))}
                            </div>
                            <div className="space-y-3 mt-2">
                                {(suggestedTimes.length > 0 || existingReservations.length > 0) && (
                                    <div data-testid="smart-reply-suggested-times" className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-3">
                                        {existingReservations.length > 0 && (
                                            <div>
                                                <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                                                    <Lock size={11} /> Reserved for this candidate (24h)
                                                </div>
                                                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="smart-reply-reservations">
                                                    {existingReservations.map((r) => (
                                                        <span key={r.id} className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 pl-2.5 py-1 pr-1 text-[11px] font-medium text-amber-800 dark:text-amber-300">
                                                            <button
                                                                type="button"
                                                                onClick={() => insertSuggestedTime(r)}
                                                                data-testid={`smart-reply-reservation-insert-${r.id}`}
                                                                title="Insert this reserved time into the draft"
                                                                className="hover:underline"
                                                            >
                                                                <Lock size={9} className="inline mr-0.5" />{r.label}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => setChatReservation({ ...r, application_id: replyFor.id })}
                                                                data-testid={`smart-reply-reservation-chat-${r.id}`}
                                                                title="Open chat with candidate"
                                                                className="ml-0.5 h-4 w-4 inline-flex items-center justify-center rounded-full hover:bg-amber-500/20"
                                                            >
                                                                <MessageCircle size={9} />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => releaseSlot(r)}
                                                                data-testid={`smart-reply-reservation-release-${r.id}`}
                                                                title="Release this reservation"
                                                                className="ml-0.5 h-4 w-4 inline-flex items-center justify-center rounded-full hover:bg-amber-500/20"
                                                            >
                                                                <Unlock size={9} />
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {suggestedTimes.length > 0 && (
                                            <div>
                                                <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                                                    <Clock size={11} /> Suggested times · click to add, lock to hold for 24h
                                                </div>
                                                <div className="mt-2 flex flex-wrap gap-1.5">
                                                    {suggestedTimes.map((slot, i) => {
                                                        const localLabel = new Date(slot.iso).toLocaleString(undefined, {
                                                            weekday: "short", month: "short", day: "numeric",
                                                            hour: "numeric", minute: "2-digit",
                                                        });
                                                        return (
                                                            <span key={slot.iso} className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-white/60 dark:bg-slate-900/40 pl-2.5 py-1 pr-1 text-[11px] font-medium text-emerald-800 dark:text-emerald-300">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => insertSuggestedTime(slot)}
                                                                    data-testid={`smart-reply-slot-${i}`}
                                                                    title={slot.label}
                                                                    className="hover:underline"
                                                                >
                                                                    {localLabel}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => reserveSlot(slot.iso)}
                                                                    disabled={reservingIso === slot.iso}
                                                                    data-testid={`smart-reply-slot-reserve-${i}`}
                                                                    title="Reserve this slot for 24h"
                                                                    className="ml-1 h-4 w-4 inline-flex items-center justify-center rounded-full hover:bg-emerald-500/20 disabled:opacity-60"
                                                                >
                                                                    {reservingIso === slot.iso
                                                                        ? <Loader2 size={9} className="animate-spin" />
                                                                        : <Lock size={9} />}
                                                                </button>
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div>
                                    <Label>Subject</Label>
                                    <Input
                                        data-testid="smart-reply-subject"
                                        value={replyDraft.subject}
                                        onChange={(e) => setReplyDraft({ ...replyDraft, subject: e.target.value })}
                                        className="mt-1.5"
                                    />
                                </div>
                                <div>
                                    <Label>Body</Label>
                                    <Textarea
                                        data-testid="smart-reply-body"
                                        rows={7}
                                        value={replyDraft.body}
                                        onChange={(e) => setReplyDraft({ ...replyDraft, body: e.target.value })}
                                        className="mt-1.5"
                                    />
                                </div>
                            </div>
                        </>
                    )}
                    <DialogFooter>
                        {bookingLink && (
                            <button
                                type="button"
                                onClick={insertBookingLink}
                                data-testid="smart-reply-insert-booking"
                                className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/15 mr-auto"
                                title={bookingLink}
                            >
                                <Calendar size={12} /> Insert booking link
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => setReplyFor(null)}
                            className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={sendReply}
                            disabled={sending || variantsLoading || variants.length === 0}
                            data-testid="smart-reply-send"
                            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                        >
                            {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                            Send & mark Contacted
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {chatReservation && (
                <ReservationChatDialog
                    reservation={chatReservation}
                    onClose={() => setChatReservation(null)}
                />
            )}

            <MessageComposeDialog
                open={!!messageTarget}
                onOpenChange={(o) => !o && setMessageTarget(null)}
                talentUserId={messageTarget?.talent_user_id}
                talentLabel={messageTarget?.name || "this candidate"}
                jobId={messageTarget?.job_id || null}
            />
        </div>
    );
};

// Employer-side chat modal on a reservation thread. Polls every 15s so the
// employer sees candidate replies without refreshing. Candidate side uses
// the same collection via the public `/book/{slug}/messages` endpoints.
export const ReservationChatDialog = ({ reservation, onClose }) => {
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [copiloting, setCopiloting] = useState(false);
    const scroller = useRef(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/employer/applications/${reservation.application_id}/reserve-slot/${reservation.id}/messages`);
            setMessages(data.results || []);
        } catch { /* silent */ }
    }, [reservation.application_id, reservation.id]);

    useEffect(() => {
        load();
        const t = setInterval(load, 15000);
        return () => clearInterval(t);
    }, [load]);

    useEffect(() => {
        if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
    }, [messages]);

    const send = async (e) => {
        e?.preventDefault?.();
        const body = draft.trim();
        if (!body) return;
        setSending(true);
        try {
            const { data } = await api.post(`/employer/applications/${reservation.application_id}/reserve-slot/${reservation.id}/messages`, { body });
            setMessages((prev) => [...prev, data.message]);
            setDraft("");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSending(false); }
    };

    const runCopilot = async () => {
        setCopiloting(true);
        try {
            const { data } = await api.post(`/employer/applications/${reservation.application_id}/reserve-slot/${reservation.id}/chat-copilot`);
            setDraft(data.draft || "");
            toast.success("Copilot draft ready — edit or send as-is.");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setCopiloting(false); }
    };

    return (
        <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
            <DialogContent data-testid="reservation-chat-dialog" className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="text-base flex items-center gap-2">
                        <MessageCircle size={14} className="text-emerald-500" /> Chat with candidate
                    </DialogTitle>
                    <DialogDescription>
                        About the reserved slot on <span className="font-semibold text-foreground">{reservation.label}</span>
                    </DialogDescription>
                </DialogHeader>
                <div ref={scroller} data-testid="reservation-chat-thread" className="max-h-[45vh] overflow-y-auto space-y-1.5 text-xs pr-1">
                    {messages.length === 0 ? (
                        <p className="text-muted-foreground italic text-center py-6">No messages yet — send the first note.</p>
                    ) : messages.map((m) => (
                        <div
                            key={m.id}
                            data-testid={`reservation-chat-msg-${m.id}`}
                            className={`flex ${m.sender === "employer" ? "justify-end" : "justify-start"}`}
                        >
                            <div className={`max-w-[80%] rounded-lg px-2.5 py-1.5 ${m.sender === "employer" ? "bg-emerald-600 text-white" : "bg-muted text-foreground border border-border"}`}>
                                <div className="whitespace-pre-wrap break-words">{m.body}</div>
                                <div className={`text-[9px] mt-0.5 flex items-center gap-1 ${m.sender === "employer" ? "text-emerald-100 justify-end" : "text-muted-foreground"}`}>
                                    <span>{new Date(m.created_at).toLocaleString(undefined, { hour: "numeric", minute: "2-digit" })}</span>
                                    {m.sender === "employer" && m.read_at && (
                                        <span data-testid={`reservation-chat-seen-${m.id}`} title={`Seen ${new Date(m.read_at).toLocaleString()}`} className="ml-0.5">✓ seen</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <form onSubmit={send} className="mt-3 flex gap-1.5">
                    <button
                        type="button"
                        onClick={runCopilot}
                        disabled={copiloting || messages.length === 0}
                        data-testid="reservation-chat-copilot"
                        title="Draft an AI reply to the latest candidate message"
                        className="inline-flex items-center gap-1 rounded-md border border-indigo-500/40 bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 px-2 text-xs hover:bg-indigo-500/20 disabled:opacity-60"
                    >
                        {copiloting ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
                    </button>
                    <input
                        type="text"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        data-testid="reservation-chat-input"
                        placeholder="Type a message…"
                        className="flex-1 text-xs rounded-md border border-border bg-background px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                    <button
                        type="submit"
                        disabled={sending || !draft.trim()}
                        data-testid="reservation-chat-send"
                        className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-3 text-xs disabled:opacity-60"
                    >
                        {sending ? <Loader2 size={11} className="animate-spin" /> : <><Send size={11} /> Send</>}
                    </button>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default EmployerApplications;
